/**
 * Vista previa comercial del catálogo (reutilizable por futuros flyers).
 * Extiende la impresión existente — no crea motor de precios.
 */
import React, { useMemo, useRef, useState, useEffect } from 'react';
import configuracionService from '../../../services/configuracion.service';
import { LISTA_PRECIO_KEYS, etiquetaLista } from '../../../utils/listasPreciosLabels';
import {
  buildCatalogoCsv,
  buildEmpresaContacto,
  downloadTextFile,
  exportCatalogoExcel,
  formatCurrencyAr,
  groupProductosByCategoria,
  resolveListaPrecio
} from '../../../utils/catalogoComercialExport';

const ListaPreciosImprimir = ({
  productos = [],
  categorias = [],
  onClose,
  listaInicial = 'mayorista',
  vigenciaDias = 30
}) => {
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [tipoLista, setTipoLista] = useState(listaInicial || 'mayorista');
  const [incluirStock, setIncluirStock] = useState(false);
  const [mostrarCodigo, setMostrarCodigo] = useState(true);
  const [mostrarImagenes, setMostrarImagenes] = useState(true);
  const [incluirPortada, setIncluirPortada] = useState(true);
  const [configEmpresa, setConfigEmpresa] = useState(null);
  const [generandoPdf, setGenerandoPdf] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    configuracionService.obtener().then(setConfigEmpresa).catch(() => setConfigEmpresa(null));
  }, []);

  const contacto = useMemo(() => buildEmpresaContacto(configEmpresa || {}), [configEmpresa]);

  const productosFiltrados = useMemo(
    () => productos.filter((p) => !filtroCategoria || p.categoria_id === filtroCategoria),
    [productos, filtroCategoria]
  );

  const productosAgrupados = useMemo(
    () => groupProductosByCategoria(productosFiltrados, categorias),
    [productosFiltrados, categorias]
  );

  const listaNombre =
    tipoLista === 'todas' ? 'Todas las listas' : etiquetaLista(configEmpresa, tipoLista);

  const fechaHoy = new Date();
  const fechaVigencia = new Date(fechaHoy);
  fechaVigencia.setDate(fechaVigencia.getDate() + Number(vigenciaDias || 30));

  const fmtFecha = (d) =>
    d.toLocaleDateString('es-AR', { year: 'numeric', month: 'long', day: 'numeric' });

  const handlePrint = () => window.print();

  const handleDownloadCSV = () => {
    const file = buildCatalogoCsv({
      productos: productosFiltrados,
      categorias,
      empresaConfig: configEmpresa,
      listaKey: tipoLista === 'todas' ? 'mayorista' : tipoLista,
      incluirStock
    });
    downloadTextFile(file);
  };

  const handleDownloadExcel = () => {
    exportCatalogoExcel({
      productos: productosFiltrados,
      categorias,
      empresaConfig: configEmpresa,
      listaKey: tipoLista,
      incluirStock
    });
  };

  const handleDownloadPDF = async () => {
    if (!printRef.current) return;
    setGenerandoPdf(true);
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const worker = html2pdf()
        .from(printRef.current)
        .set({
          margin: [12, 10, 14, 10],
          filename: `Catalogo_${listaNombre.replace(/\s+/g, '_')}_${fechaHoy.toISOString().slice(0, 10)}.pdf`,
          html2canvas: { scale: 2, useCORS: true, logging: false },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['css', 'legacy'] }
        })
        .toPdf()
        .get('pdf')
        .then((pdf) => {
          const total = pdf.internal.getNumberOfPages();
          for (let i = 1; i <= total; i += 1) {
            pdf.setPage(i);
            pdf.setFontSize(8);
            pdf.setTextColor(100);
            const w = pdf.internal.pageSize.getWidth();
            const h = pdf.internal.pageSize.getHeight();
            pdf.text(
              `${contacto.nombre} · ${listaNombre} · Página ${i} de ${total}`,
              w / 2,
              h - 6,
              { align: 'center' }
            );
          }
        });
      await worker.save();
    } catch (err) {
      console.error('Error PDF catálogo:', err);
      alert('No se pudo generar el PDF');
    } finally {
      setGenerandoPdf(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-100 z-50 overflow-auto">
      <div className="print:hidden sticky top-0 bg-white shadow-md p-4 mb-4 z-10">
        <div className="max-w-6xl mx-auto space-y-4">
          <div className="flex justify-between items-center gap-3">
            <div>
              <h2 className="text-2xl font-bold text-slate-800">Vista previa comercial</h2>
              <p className="text-sm text-slate-500">
                {productosFiltrados.length} productos · lista {listaNombre}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-slate-500 hover:text-slate-800 text-3xl leading-none px-2"
              aria-label="Cerrar"
            >
              ×
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <select
              value={filtroCategoria}
              onChange={(e) => setFiltroCategoria(e.target.value)}
              className="border rounded-lg px-3 py-3 min-h-[48px]"
            >
              <option value="">Todas las categorías</option>
              {categorias.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.nombre}
                </option>
              ))}
            </select>

            <select
              value={tipoLista}
              onChange={(e) => setTipoLista(e.target.value)}
              className="border rounded-lg px-3 py-3 min-h-[48px]"
            >
              {LISTA_PRECIO_KEYS.map((key) => (
                <option key={key} value={key}>
                  {etiquetaLista(configEmpresa, key)}
                </option>
              ))}
              <option value="todas">Todas las listas</option>
            </select>

            <label className="flex items-center gap-2 border rounded-lg px-3 py-3 min-h-[48px] bg-slate-50">
              <input
                type="checkbox"
                checked={incluirPortada}
                onChange={(e) => setIncluirPortada(e.target.checked)}
              />
              <span className="text-sm">Portada</span>
            </label>
            <label className="flex items-center gap-2 border rounded-lg px-3 py-3 min-h-[48px] bg-slate-50">
              <input
                type="checkbox"
                checked={incluirStock}
                onChange={(e) => setIncluirStock(e.target.checked)}
              />
              <span className="text-sm">Stock</span>
            </label>
            <label className="flex items-center gap-2 border rounded-lg px-3 py-3 min-h-[48px] bg-slate-50">
              <input
                type="checkbox"
                checked={mostrarCodigo}
                onChange={(e) => setMostrarCodigo(e.target.checked)}
              />
              <span className="text-sm">Código</span>
            </label>
            <label className="flex items-center gap-2 border rounded-lg px-3 py-3 min-h-[48px] bg-slate-50">
              <input
                type="checkbox"
                checked={mostrarImagenes}
                onChange={(e) => setMostrarImagenes(e.target.checked)}
              />
              <span className="text-sm">Imágenes</span>
            </label>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handlePrint}
              className="min-h-[48px] px-5 rounded-lg bg-slate-800 text-white font-medium"
            >
              Imprimir
            </button>
            <button
              type="button"
              onClick={handleDownloadPDF}
              disabled={generandoPdf}
              className="min-h-[48px] px-5 rounded-lg bg-blue-600 text-white font-medium disabled:opacity-60"
            >
              {generandoPdf ? 'Generando PDF…' : 'Exportar PDF'}
            </button>
            <button
              type="button"
              onClick={handleDownloadExcel}
              className="min-h-[48px] px-5 rounded-lg bg-emerald-600 text-white font-medium"
            >
              Excel
            </button>
            <button
              type="button"
              onClick={handleDownloadCSV}
              className="min-h-[48px] px-5 rounded-lg bg-teal-600 text-white font-medium"
            >
              CSV
            </button>
          </div>
        </div>
      </div>

      <div ref={printRef} className="max-w-4xl mx-auto bg-white text-slate-900 shadow-lg mb-10">
        {incluirPortada && (
          <section className="catalogo-portada min-h-[90vh] flex flex-col justify-between p-10 border-b page-break-after">
            <div className="text-center space-y-4 pt-16">
              {contacto.logo ? (
                <img
                  src={contacto.logo}
                  alt="Logo"
                  crossOrigin="anonymous"
                  className="mx-auto max-h-24 object-contain"
                />
              ) : null}
              <h1 className="text-4xl font-bold tracking-tight">{contacto.nombre}</h1>
              <p className="text-xl text-slate-600">Catálogo comercial</p>
              <p className="text-2xl font-semibold text-slate-800 mt-6">{listaNombre}</p>
              <p className="text-slate-500">
                {productosFiltrados.length} productos · Vigencia hasta {fmtFecha(fechaVigencia)}
              </p>
            </div>
            <div className="flex items-end justify-between gap-6 pt-10">
              <div className="text-sm text-slate-600 space-y-1 text-left">
                {contacto.direccion ? <p>{contacto.direccion}</p> : null}
                {contacto.whatsapp ? <p>WhatsApp: {contacto.whatsapp}</p> : null}
                {contacto.email ? <p>{contacto.email}</p> : null}
                {contacto.web ? <p>{contacto.web}</p> : null}
                <p className="pt-2">Fecha: {fmtFecha(fechaHoy)}</p>
              </div>
              {contacto.qrImageUrl ? (
                <div className="text-center">
                  <img src={contacto.qrImageUrl} alt="QR contacto" className="w-28 h-28 mx-auto" />
                  <p className="text-xs text-slate-500 mt-1">Contacto</p>
                </div>
              ) : null}
            </div>
          </section>
        )}

        <header className="px-8 pt-8 pb-4 border-b-2 border-slate-800">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              {contacto.logo ? (
                <img
                  src={contacto.logo}
                  alt=""
                  crossOrigin="anonymous"
                  className="max-h-14 object-contain"
                />
              ) : null}
              <div>
                <h2 className="text-xl font-bold">{contacto.nombre}</h2>
                <p className="text-sm text-slate-600">{listaNombre}</p>
                <p className="text-xs text-slate-500">
                  {fmtFecha(fechaHoy)} · Vigencia hasta {fmtFecha(fechaVigencia)} ·{' '}
                  {productosFiltrados.length} productos
                </p>
              </div>
            </div>
            {contacto.qrImageUrl ? (
              <img src={contacto.qrImageUrl} alt="QR" className="w-16 h-16" />
            ) : null}
          </div>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
            {contacto.direccion ? <span>{contacto.direccion}</span> : null}
            {contacto.whatsapp ? <span>WA {contacto.whatsapp}</span> : null}
            {contacto.email ? <span>{contacto.email}</span> : null}
            {contacto.web ? <span>{contacto.web}</span> : null}
          </div>
        </header>

        <div className="px-8 py-6 space-y-8">
          {Object.entries(productosAgrupados).map(([categoria, prods]) => (
            <section key={categoria} className="avoid-break">
              <h3 className="text-sm font-bold uppercase tracking-wide border-b border-slate-300 pb-1 mb-3">
                {categoria}
              </h3>
              <table className="w-full text-sm catalogo-table">
                <thead>
                  <tr className="bg-slate-100">
                    {mostrarImagenes ? <th className="py-2 px-2 w-14" /> : null}
                    {mostrarCodigo ? (
                      <th className="text-left py-2 px-2 font-semibold">Código</th>
                    ) : null}
                    <th className="text-left py-2 px-2 font-semibold">Producto</th>
                    {tipoLista === 'todas' ? (
                      LISTA_PRECIO_KEYS.map((key) => (
                        <th key={key} className="text-right py-2 px-2 font-semibold">
                          {etiquetaLista(configEmpresa, key)}
                        </th>
                      ))
                    ) : (
                      <th className="text-right py-2 px-2 font-semibold">Precio</th>
                    )}
                    {incluirStock ? (
                      <th className="text-center py-2 px-2 font-semibold">Stock</th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {prods.map((producto, index) => (
                    <tr
                      key={producto.id || `${categoria}-${index}`}
                      className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}
                    >
                      {mostrarImagenes ? (
                        <td className="py-1.5 px-2">
                          {producto.imagen ? (
                            <img
                              src={producto.imagen}
                              alt=""
                              crossOrigin="anonymous"
                              className="w-10 h-10 object-cover rounded"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-slate-200 rounded" />
                          )}
                        </td>
                      ) : null}
                      {mostrarCodigo ? (
                        <td className="py-1.5 px-2 text-slate-600 whitespace-nowrap">
                          {producto.codigo || '—'}
                        </td>
                      ) : null}
                      <td className="py-1.5 px-2 font-medium">{producto.nombre}</td>
                      {tipoLista === 'todas' ? (
                        LISTA_PRECIO_KEYS.map((key) => (
                          <td key={key} className="text-right py-1.5 px-2 font-semibold whitespace-nowrap">
                            {formatCurrencyAr(resolveListaPrecio(producto, key))}
                          </td>
                        ))
                      ) : (
                        <td className="text-right py-1.5 px-2 font-semibold whitespace-nowrap">
                          {formatCurrencyAr(resolveListaPrecio(producto, tipoLista))}
                        </td>
                      )}
                      {incluirStock ? (
                        <td className="text-center py-1.5 px-2">{producto.stock_actual ?? 0}</td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
        </div>

        <footer className="px-8 py-6 border-t text-center text-xs text-slate-500 space-y-1">
          <p>
            Precios en pesos argentinos · sujetos a modificación · vigencia hasta{' '}
            {fmtFecha(fechaVigencia)}
          </p>
          <p>
            {contacto.nombre}
            {contacto.whatsapp ? ` · WhatsApp ${contacto.whatsapp}` : ''}
            {contacto.web ? ` · ${contacto.web}` : ''}
          </p>
        </footer>
      </div>

      <style>{`
@page { size: A4 portrait; margin: 10mm; }
@media print {
  .print\\:hidden { display: none !important; }
  body { background: white !important; }
  .page-break-after { page-break-after: always; break-after: page; }
  .avoid-break { break-inside: avoid; page-break-inside: avoid; }
  .catalogo-table { font-size: 10px; border-collapse: collapse; width: 100%; }
  .catalogo-table th, .catalogo-table td { border-bottom: 1px solid #e2e8f0; padding: 4px 6px; }
  .catalogo-table th { background: #f1f5f9 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}
.page-break-after { page-break-after: always; break-after: page; }
`}</style>
    </div>
  );
};

export default ListaPreciosImprimir;
